import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon, EyeIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, CodeBracketIcon } from '@heroicons/react/24/outline';
import campaignService from '../../services/campaignService';

interface PreviewData {
  id: number;
  name: string;
  subject: string;
  content_html: string;
  content_text?: string;
  segment_name?: string;
  estimated_recipients: number;
  approval_status: string;
  created_at: string;
}

const PreviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<{ approved: boolean; message: string } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [showCampaignDetails, setShowCampaignDetails] = useState(true);

  const loadPreview = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await campaignService.getPreviewByToken(token);
      setPreviewData(data);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadPreview();
    }
  }, [token, loadPreview]);

  const handleApproval = async (approved: boolean) => {
    if (!token) return;

    // Email is now optional - no validation required

    if (approved && !feedback.trim()) {
      setPendingApproval(true);
      setShowConfirmDialog(true);
      return;
    }

    try {
      setSubmitting(true);
      const result = await campaignService.processApproval(token, approved, feedback.trim() || undefined, email.trim() || undefined);
      
      setSubmitted(true);
      setSubmissionResult({
        approved,
        message: result.message
      });
    } catch (err) {
      console.error('Error processing approval:', err);
      alert(err instanceof Error ? err.message : 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmApproval = async () => {
    setShowConfirmDialog(false);
    
    try {
      setSubmitting(true);
      const result = await campaignService.processApproval(token!, pendingApproval, feedback.trim() || undefined, email.trim() || undefined);
      
      setSubmitted(true);
      setSubmissionResult({
        approved: pendingApproval,
        message: result.message
      });
    } catch (err) {
      console.error('Error processing approval:', err);
      alert(err instanceof Error ? err.message : 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmDialog(false);
    setPendingApproval(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract body content from full HTML document or return as-is for fragments
  const extractBodyContent = (html: string): string => {
    // Check if it's a full HTML document with body tags
    if (html.includes('<body') || html.includes('<BODY')) {
      // Extract everything between body tags
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (bodyMatch && bodyMatch[1]) {
        return bodyMatch[1].trim();
      }
    }
    // If no body tags found, return the HTML as is
    return html;
  };

  // Extract styles from HTML document
  const extractStyles = (html: string): string => {
    const styles: string[] = [];
    
    // Extract style tags
    const styleMatches = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
    if (styleMatches) {
      styleMatches.forEach(match => {
        const styleContent = match.replace(/<\/?style[^>]*>/gi, '');
        styles.push(styleContent);
      });
    }
    
    return styles.join('\n');
  };

  const generateSampleContent = (content: string) => {
    // Extract body content first
    const bodyContent = extractBodyContent(content);
    
    // Generate preview with sample data and highlighted personalization variables
    return bodyContent.replace(/\{\{(\w+)\}\}/g, (match: string, key: string) => {
      const sampleData: Record<string, string> = {
        parent_name: 'Sarah Johnson',
        child_name: 'Emma',
        child_age: '8',
        timeline: 'Next semester',
        special_needs: 'Autism spectrum support',
        school_name: 'Bright Futures Academy',
        campaign_name: previewData?.name || 'Campaign Name'
      };
      return `<span style="background-color: #fef3c7; padding: 2px 4px; border-radius: 4px; font-weight: 600;">${sampleData[key] || match}</span>`;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaign preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <XMarkIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Not Found</h3>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <p className="text-xs text-gray-500">
              This preview link may have expired or been removed. Please contact the sender for a new link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted && submissionResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            {submissionResult.approved ? (
              <CheckIcon className="h-12 w-12 text-green-400 mx-auto mb-4" />
            ) : (
              <XMarkIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
            )}
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {submissionResult.approved ? 'Campaign Approved!' : 'Campaign Rejected'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {submissionResult.message}
            </p>
            <p className="text-xs text-gray-500">
              The campaign creator has been notified of your decision.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!previewData) {
    return null;
  }

  const sampleContent = generateSampleContent(previewData.content_html);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Campaign Preview</h1>
              <p className="text-sm sm:text-sm text-gray-600 mt-1">
                Please review and approve or request changes
              </p>
            </div>
            <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-500">
              <ClockIcon className="h-4 w-4" />
              <span>Created {formatDate(previewData.created_at)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Campaign Details - Collapsible */}
        <div className="mb-6">
          <button
            onClick={() => setShowCampaignDetails(!showCampaignDetails)}
            className="w-full bg-white rounded-lg shadow-sm p-4 sm:p-4 flex items-center justify-between hover:bg-gray-50 min-h-[44px]"
          >
            <h3 className="text-lg font-medium text-gray-900">Campaign Details</h3>
            {showCampaignDetails ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </button>
          
          {showCampaignDetails && (
            <div className="bg-white rounded-b-lg shadow-sm border-t border-gray-100 p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {/* Approval Form - First on mobile, last on desktop */}
                <div className="order-first md:order-last">
                  <h4 className="text-base sm:text-sm font-medium text-gray-900 mb-4">Your Response</h4>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Email (optional)
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="text-base sm:text-sm mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3 sm:py-2"
                        placeholder="your@email.com (optional)"
                      />
                    </div>

                    <div>
                      <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                        Comments (optional)
                      </label>
                      <textarea
                        id="feedback"
                        rows={3}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        className="text-base sm:text-sm mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3 sm:py-2"
                        placeholder="Any feedback or changes you'd like..."
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      onClick={() => handleApproval(true)}
                      disabled={submitting}
                      className="min-h-[44px] flex-1 inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-base sm:text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <CheckIcon className="h-5 w-5 sm:h-4 sm:w-4 mr-2 sm:mr-1" />
                      {submitting ? 'Processing...' : 'Approve'}
                    </button>

                    <button
                      onClick={() => handleApproval(false)}
                      disabled={submitting}
                      className="min-h-[44px] flex-1 inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-300 text-base sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <XMarkIcon className="h-5 w-5 sm:h-4 sm:w-4 mr-2 sm:mr-1" />
                      {submitting ? 'Processing...' : 'Request Changes'}
                    </button>
                  </div>
                </div>

                {/* Campaign Info */}
                <div className="order-2 md:order-1">
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Campaign Name</dt>
                      <dd className="text-sm text-gray-900 mt-1 break-words">{previewData.name}</dd>
                    </div>

                    {previewData.segment_name && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Target Segment</dt>
                        <dd className="text-sm text-gray-900 mt-1">{previewData.segment_name}</dd>
                      </div>
                    )}

                    <div>
                      <dt className="text-sm font-medium text-gray-500">Estimated Recipients</dt>
                      <dd className="text-sm text-gray-900 mt-1">{previewData.estimated_recipients} families</dd>
                    </div>
                  </dl>
                </div>

                {/* Status Info */}
                <div className="order-3 md:order-2">
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Current Status</dt>
                      <dd className="text-sm mt-1">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Pending Approval
                        </span>
                      </dd>
                    </div>
                    <div className="block sm:hidden">
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="text-sm text-gray-900 mt-1">{formatDate(previewData.created_at)}</dd>
                    </div>
                    <div className="hidden sm:block">
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="text-sm text-gray-900 mt-1">{formatDate(previewData.created_at)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Large Email Preview */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 sm:gap-0">
              <h3 className="text-lg sm:text-xl font-medium text-gray-900">Email Preview</h3>
              <div className="flex items-center text-sm text-gray-500">
                <EyeIcon className="h-4 w-4 mr-1" />
                Preview with sample data
              </div>
            </div>
            <button
              onClick={() => setShowHtmlSource(!showHtmlSource)}
              className="hidden sm:inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <CodeBracketIcon className="h-4 w-4 mr-1" />
              {showHtmlSource ? 'Hide HTML' : 'View HTML'}
            </button>
          </div>
          
          {/* Email Subject Preview */}
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border">
              <div className="text-xs text-gray-500 mb-1">Subject Line:</div>
              <div className="font-medium text-gray-900 text-base sm:text-lg break-words">{previewData.subject}</div>
            </div>
          </div>

          {/* Email Content Preview */}
          <div className="p-4 sm:p-6">
            {!showHtmlSource ? (
              /* Visual Preview - Mobile Optimized */
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 sm:px-6 py-3 border-b">
                  <div className="text-sm text-gray-600">
                    How this email will look to recipients (using sample parent/child data)
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <div className="p-4 sm:p-8 bg-white min-h-[400px] sm:min-h-[600px] min-w-[320px]">
                  {/* Default email styles for proper rendering */}
                  <style dangerouslySetInnerHTML={{ __html: `
                    .email-preview-content {
                      font-family: Arial, sans-serif;
                      line-height: 1.6;
                      color: #333;
                      font-size: 16px;
                    }
                    .email-preview-content h1 {
                      font-size: 28px;
                      font-weight: bold;
                      margin: 20px 0 15px 0;
                      color: #111;
                    }
                    .email-preview-content h2 {
                      font-size: 22px;
                      font-weight: bold;
                      margin: 18px 0 12px 0;
                      color: #222;
                    }
                    .email-preview-content h3 {
                      font-size: 18px;
                      font-weight: bold;
                      margin: 15px 0 10px 0;
                      color: #333;
                    }
                    .email-preview-content p {
                      margin: 10px 0;
                      line-height: 1.6;
                    }
                    .email-preview-content ul, .email-preview-content ol {
                      margin: 10px 0;
                      padding-left: 30px;
                    }
                    .email-preview-content li {
                      margin: 5px 0;
                    }
                    .email-preview-content strong {
                      font-weight: bold;
                    }
                    .email-preview-content em {
                      font-style: italic;
                    }
                    .email-preview-content a {
                      color: #0066cc;
                      text-decoration: underline;
                    }
                    .email-preview-content blockquote {
                      margin: 15px 0;
                      padding: 10px 20px;
                      border-left: 3px solid #ddd;
                      background: #f9f9f9;
                    }
                    .email-preview-content table {
                      border-collapse: collapse;
                      width: 100%;
                      margin: 15px 0;
                    }
                    .email-preview-content th, .email-preview-content td {
                      border: 1px solid #ddd;
                      padding: 8px;
                      text-align: left;
                    }
                    .email-preview-content th {
                      background-color: #f2f2f2;
                      font-weight: bold;
                    }
                    ${extractStyles(previewData.content_html)}
                  ` }} />
                  
                  <div 
                    className="email-preview-content"
                    dangerouslySetInnerHTML={{ __html: sampleContent }}
                  />
                  
                  {/* Check if full HTML document was provided */}
                  {(previewData.content_html.includes('<body') || previewData.content_html.includes('<!DOCTYPE')) && (
                    <div className="mt-4 text-xs text-gray-500 border-t pt-2">
                      ℹ️ Rendering body content from full HTML document
                    </div>
                  )}
                  </div>
                </div>
              </div>
            ) : (
              /* HTML Source View */
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-700 mb-3">HTML Source</h4>
                  <div className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-auto max-h-96">
                    <pre className="text-sm font-mono whitespace-pre-wrap">{previewData.content_html}</pre>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-700 mb-3">Rendered Preview</h4>
                  <div className="border rounded-lg p-6 bg-white">
                    {/* Default email styles for proper rendering */}
                    <style dangerouslySetInnerHTML={{ __html: `
                      .email-source-preview {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        color: #333;
                        font-size: 16px;
                      }
                      .email-source-preview h1 {
                        font-size: 28px;
                        font-weight: bold;
                        margin: 20px 0 15px 0;
                        color: #111;
                      }
                      .email-source-preview h2 {
                        font-size: 22px;
                        font-weight: bold;
                        margin: 18px 0 12px 0;
                        color: #222;
                      }
                      .email-source-preview h3 {
                        font-size: 18px;
                        font-weight: bold;
                        margin: 15px 0 10px 0;
                        color: #333;
                      }
                      .email-source-preview p {
                        margin: 10px 0;
                        line-height: 1.6;
                      }
                      .email-source-preview ul, .email-source-preview ol {
                        margin: 10px 0;
                        padding-left: 30px;
                      }
                      .email-source-preview li {
                        margin: 5px 0;
                      }
                      .email-source-preview strong {
                        font-weight: bold;
                      }
                      .email-source-preview em {
                        font-style: italic;
                      }
                      .email-source-preview a {
                        color: #0066cc;
                        text-decoration: underline;
                      }
                      .email-source-preview blockquote {
                        margin: 15px 0;
                        padding: 10px 20px;
                        border-left: 3px solid #ddd;
                        background: #f9f9f9;
                      }
                      ${extractStyles(previewData.content_html)}
                    ` }} />
                    
                    <div 
                      className="email-source-preview"
                      dangerouslySetInnerHTML={{ __html: sampleContent }}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Plain Text Version (if available) */}
            {previewData.content_text && showHtmlSource && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-700 mb-3">Plain Text Version</h4>
                <div className="bg-gray-50 p-6 rounded-lg text-sm text-gray-700 font-mono whitespace-pre-wrap max-h-64 overflow-auto">
                  {campaignService.generatePreviewContent(previewData.content_text)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Approve Without Comments</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to approve this campaign without any comments or feedback?
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmApproval}
                  disabled={submitting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {submitting ? 'Processing...' : 'Yes, Approve'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelConfirmation}
                  disabled={submitting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewPage;